class MobileZoomModal {
  constructor(options = {}) {
    this.modal = null;
    this.isOpen = false;
    this.zoomImage = null;
    
    this.config = {
      modalClass: 'mobile-zoom-modal',
      overlayClass: 'mobile-zoom-overlay',
      contentClass: 'mobile-zoom-content',
      closeClass: 'mobile-zoom-close',
      imageClass: 'mobile-zoom-image',
      ...options
    };

    this.bindEvents();
  }

  bindEvents() {
    // Close modal on Escape key (for desktop testing)
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });
  }

  getDetailImage() {
    // Try to get the metafield value from the product page
    // Method 1: Look for a meta tag or data attribute
    const metaElement = document.querySelector('meta[name="product-detail-image"]');
    if (metaElement) {
      return metaElement.content;
    }

    // Method 2: Look for a script tag with product data
    const productScript = document.querySelector('script[type="application/json"][data-product-json]');
    if (productScript) {
      try {
        const productData = JSON.parse(productScript.textContent);
        if (productData.metafields && productData.metafields.custom && productData.metafields.custom.detail_bild) {
          return productData.metafields.custom.detail_bild;
        }
      } catch (error) {
        console.error('Error parsing product data:', error);
      }
    }

    // Method 3: Look for a data attribute on the product form or container
    const productContainer = document.querySelector('[data-detail-image]');
    if (productContainer) {
      return productContainer.dataset.detailImage;
    }

    // Method 4: Check global window variables (common in Shopify themes)
    if (window.product && window.product.metafields && window.product.metafields.custom && window.product.metafields.custom.detail_bild) {
      return window.product.metafields.custom.detail_bild;
    }

    // Method 5: Check ShopifyAnalytics
    if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.product) {
      const product = window.ShopifyAnalytics.meta.product;
      if (product.metafields && product.metafields.custom && product.metafields.custom.detail_bild) {
        return product.metafields.custom.detail_bild;
      }
    }

    console.warn('Detail image metafield (custom.detail_bild) not found');
    return null;
  }

  createModal() {
    if (this.modal) return;

    const detailImageUrl = this.getDetailImage();
    
    if (!detailImageUrl) {
      console.error('No detail image found. Cannot create zoom modal.');
      return;
    }

    // Create modal overlay
    this.modal = document.createElement('div');
    this.modal.className = this.config.modalClass;
    this.modal.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.95);
      z-index: 9999;
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 0;
      visibility: hidden;
      transition: all 0.3s ease;
      touch-action: manipulation;
    `;

    // Create close button - larger for mobile
    const closeButton = document.createElement('button');
    closeButton.className = this.config.closeClass;
    closeButton.innerHTML = `
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <line x1="18" y1="6" x2="6" y2="18" stroke="white" stroke-width="3" stroke-linecap="round"/>
        <line x1="6" y1="6" x2="18" y2="18" stroke="white" stroke-width="3" stroke-linecap="round"/>
      </svg>
    `;
    closeButton.style.cssText = `
      position: absolute;
      top: 20px;
      right: 20px;
      background: rgba(0, 0, 0, 0.7);
      border: none;
      border-radius: 50%;
      width: 40px;
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      z-index: 10001;
      transition: all 0.3s ease;
      backdrop-filter: blur(4px);
      border: 2px solid rgba(255, 255, 255, 0.3);
      touch-action: manipulation;
    `;

    // Mobile-friendly touch events for close button
    closeButton.addEventListener('touchstart', (e) => {
      e.preventDefault();
      closeButton.style.background = 'rgba(0, 0, 0, 0.9)';
      closeButton.style.transform = 'scale(1.1)';
    }, { passive: false });

    closeButton.addEventListener('touchend', (e) => {
      e.preventDefault();
      e.stopPropagation();
      closeButton.style.background = 'rgba(0, 0, 0, 0.7)';
      closeButton.style.transform = 'scale(1)';
      this.close();
    }, { passive: false });

    // Fallback click event
    closeButton.addEventListener('click', (e) => {
      e.stopPropagation();
      this.close();
    });

    // Create image container - mobile optimized
    const imageContainer = document.createElement('div');
    imageContainer.className = this.config.contentClass;
    imageContainer.style.cssText = `
      position: relative;
      width: 90vw;
      height: 60vh;
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 20px;
      border-radius: 12px;
      backdrop-filter: blur(10px);
    `;

    // Create image element - mobile optimized
    this.zoomImage = document.createElement('img');
    this.zoomImage.className = this.config.imageClass;
    this.zoomImage.src = detailImageUrl;
    this.zoomImage.alt = 'Product Detail View';
    this.zoomImage.style.cssText = `
      max-width: 100%;
      max-height: 100%;
      width: auto;
      height: auto;
      object-fit: contain;
      border-radius: 8px;
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
      touch-action: manipulation;
    `;

    // Add loading state
    this.zoomImage.style.opacity = '0';
    this.zoomImage.addEventListener('load', () => {
      this.zoomImage.style.opacity = '1';
      this.zoomImage.style.transition = 'opacity 0.3s ease';
    });

    this.zoomImage.addEventListener('error', () => {
      console.error('Failed to load detail image');
      this.close();
    });

    // Assemble modal
    imageContainer.appendChild(this.zoomImage);
    this.modal.appendChild(closeButton);
    this.modal.appendChild(imageContainer);

    // Add touch-to-close on overlay (mobile-friendly)
    this.modal.addEventListener('touchstart', (e) => {
      if (e.target === this.modal) {
        this.close();
      }
    }, { passive: true });

    // Prevent image touch from closing modal
    this.zoomImage.addEventListener('touchstart', (e) => {
      e.stopPropagation();
    }, { passive: true });

    imageContainer.addEventListener('touchstart', (e) => {
      e.stopPropagation();
    }, { passive: true });

    // Add click-to-close on overlay (desktop fallback)
    this.modal.addEventListener('click', (e) => {
      if (e.target === this.modal) {
        this.close();
      }
    });

    // Prevent image click from closing modal
    this.zoomImage.addEventListener('click', (e) => {
      e.stopPropagation();
    });

    imageContainer.addEventListener('click', (e) => {
      e.stopPropagation();
    });

    document.body.appendChild(this.modal);
  }

  open() {
    this.createModal();
    
    if (!this.modal) return;

    this.isOpen = true;
    
    // Prevent body scroll - important for mobile
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    
    // Animate in
    requestAnimationFrame(() => {
      this.modal.style.opacity = '1';
      this.modal.style.visibility = 'visible';
    });
  }

  close() {
    if (!this.modal || !this.isOpen) return;

    this.isOpen = false;
    
    // Restore body scroll - important for mobile
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.width = '';
    
    // Animate out
    this.modal.style.opacity = '0';
    this.modal.style.visibility = 'hidden';
    
    // Remove from DOM after animation
    setTimeout(() => {
      if (this.modal && this.modal.parentNode) {
        this.modal.parentNode.removeChild(this.modal);
        this.modal = null;
        this.zoomImage = null;
      }
    }, 300);
  }

  destroy() {
    this.close();
  }
}

// Export for use
window.MobileZoomModal = MobileZoomModal;